// Aceleres — porteiro de primeiro acesso: quem nao tem nenhuma conexao
// cadastrada entra pela tela de Integracoes, e nao por Conversas vazias.
//
// O sinal é a AUSENCIA DE CADASTRO, nunca o status da conexao. A distincao é
// cara: em 19/08/2026 um gate que olhava "canal conectado"
// (`companyHasConnectedChannel`) trancou usuarios do lado de fora justamente
// quando a ultima instancia caia — ou seja, no unico momento em que eles
// precisavam entrar para reconectar. Uma instancia cadastrada e offline
// continua cadastrada e nao aciona este gate. Se alguem trouxer `status` para
// dentro deste arquivo, é a regressao daquele bug voltando.
//
// Fail-open em todas as bordas: bridge fora do ar, timeout, payload
// inesperado ou conta ausente deixam a navegacao seguir. Um porteiro que erra
// para o lado do bloqueio repete o incidente acima.
import { routeIsAccessibleFor } from './routeHelpers';
import { getUserPermissions } from './permissionsHelper';
import {
  getIntegracoesPageData,
  prefetchIntegracoesPageData,
} from '../routes/dashboard/configuracoes/integracoesPageData';

export const INTEGRACOES_ROUTE = 'configuracoes_integracoes';

// Quem nao passa por aqui seria devolvido por `validateLoggedInRoutes` para o
// dashboard, e o gate viraria ping-pong entre as duas telas.
//
// ATENCAO: isto e DELIBERADAMENTE mais estreito que o `meta` da rota em
// configuracoes.routes.js, que desde 22/08/2026 tambem aceita `agent`. O
// vendedor ALCANCA Integracoes; o que ele nao faz e ser DESVIADO para la no
// primeiro acesso — o porteiro continua sendo so para gestor. Ampliar esta
// lista para `agent` muda o destino de login de todo vendedor sem conexao, o
// que e outra decisao de produto; nao a tome por simetria com a rota.
const INTEGRACOES_ROUTE_META = {
  meta: {
    permissions: ['administrator', 'integration_manage'],
    pagePermission: 'page_integrations',
  },
};

// Rotas que precisam continuar alcancaveis mesmo sem conexao: a propria tela
// de destino, o assistente que cadastra a primeira conexao, e as telas de
// borda da sessao.
const EXEMPT_ROUTES = new Set([
  INTEGRACOES_ROUTE,
  'no_accounts',
  'account_suspended',
  'login',
]);

// Teto de espera da consulta a ponte. A guarda roda antes de pintar a tela;
// esperar indefinidamente transformaria uma ponte lenta em app travado.
const LOOKUP_TIMEOUT_MS = 5000;

const withTimeout = promise =>
  Promise.race([
    promise,
    new Promise(resolve => {
      setTimeout(() => resolve(null), LOOKUP_TIMEOUT_MS);
    }),
  ]);

export const canReachIntegracoes = (user, accountId) => {
  try {
    return routeIsAccessibleFor(
      INTEGRACOES_ROUTE_META,
      getUserPermissions(user, accountId)
    );
  } catch (error) {
    return false;
  }
};

/**
 * Devolve true quando a navegacao deve ser desviada para Integracoes.
 *
 * `whatsapp.instances` vem de `IntegracoesBridgeController` (Laravel) e ja
 * chega recortado por quem olha: conexao compartilhada aparece para a empresa
 * toda, pessoal de terceiro nao aparece. Lista vazia significa, de verdade,
 * que aquela pessoa nao tem canal nenhum com que trabalhar.
 */
export const shouldForceIntegracoes = async ({
  routeName,
  user,
  accountId,
} = {}) => {
  if (!user || !accountId) return false;
  if (routeName && EXEMPT_ROUTES.has(routeName)) return false;
  if (!canReachIntegracoes(user, accountId)) return false;

  let payload = getIntegracoesPageData(accountId);

  if (!payload) {
    payload = await withTimeout(
      prefetchIntegracoesPageData(accountId).catch(() => null)
    );
  }

  // `payload` é o cache da pagina: { accountId, whatsapp, sellers }. A lista
  // de conexoes mora dentro de `whatsapp`, que é a resposta crua da ponte.
  const instances = payload?.whatsapp?.instances;

  // Sem resposta utilizavel nao se decide nada: passa.
  if (!Array.isArray(instances)) return false;

  return instances.length === 0;
};
